import React, { useMemo } from "react";
import type { ColourType } from "../../../../../../common-deprecated/types/CommonTypes";
import * as Styles from "./styles/ItemSlider";
import { isLightColour } from "../../../../../../common-deprecated/utils";
import { isItemFirstOfType } from "../../../utils/sliderUtils";
import type { ItemSliderPropsType } from "./ItemSlider";
import ItemSlider from "./ItemSlider";
import * as Flex from "../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import useDynamicItemSlider from "../../../../../../common-deprecated/hooks/useItemSliderSinglePage";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useBnbSelector } from "../../../redux/store";
import SrOnly from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";

/**
 * This is a static version of the ItemSlider.
 * It takes the same arguments and renders the same elements minus the slider logic.
 * The list element will shrink based on the amount of items, which makes it useful when there are only a small number of them.
 */
const StaticItemSlider = ({ items, activeItem, activeItemType, selectItem }: ItemSliderPropsType): JSX.Element => {
    const renderedItems = useMemo(
        () => (
            <>
                {items.map((item, index) => {
                    const lightColour = !!(item as ColourType).rgb && isLightColour((item as ColourType).rgb);
                    const showDivider = index > 0 && isItemFirstOfType(items, index);
                    const isSelected = !!activeItem && item.id === activeItem.id;
                    return (
                        <Styles.ItemWrapper key={item.id} onClick={() => selectItem(item)} showDivider={showDivider}>
                            <Styles.ExteriorImage
                                image={item.image}
                                rgb={(item as ColourType).rgb}
                                isSelected={isSelected}
                                zoomedIn={activeItemType === "colour"}
                                lightColour={lightColour}
                            />
                            <SrOnly>{item.name}</SrOnly>
                        </Styles.ItemWrapper>
                    );
                })}
            </>
        ),
        // OR-9070: We added items.length because array gets re-assigned every time and this prevents pointless re-renders. However if the case ever occurs where a bug is reported that the items don't update when the length remains, please modify as follows:
        // declare const itemIds = items.map({id} => id).join() outside of this useMemo but in the component scope
        // replace items.length with the itemIds
        // alternatives are possible but that seems efficient to me
        [activeItem, activeItemType, items.length],
    );

    return (
        <Styles.Wrapper>
            <Styles.Slider>
                <Styles.SliderViewport>{renderedItems}</Styles.SliderViewport>
            </Styles.Slider>
        </Styles.Wrapper>
    );
};

const DynamicItemSlider = (props: ItemSliderPropsType): JSX.Element => {
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const displayStaticSlider = useDynamicItemSlider(props.items.length, Styles.TOTAL_WIDTH, 1440, 30, 2 / 3);
    return displayStaticSlider ? (
        <Flex.Col
            mt={4}
            mb={{ xs: 2, lg: 4 }}
            pl={isRTL(commonSettings) ? "15px" : 0}
            pr={isRTL(commonSettings) ? 0 : "15px"}
            width={{ xs: 1, lg: "initial" }}
            maxWidth={{ lg: "66.666%" }} // maxWidth does not parse fractions as %
        >
            <StaticItemSlider {...props} />
        </Flex.Col>
    ) : (
        <Flex.Col
            mt={5}
            mb={{ xs: 2, lg: 5 }}
            px={0}
            width={{ xs: 1, lg: 2 / 3 }}
            flexGrow={1}
            maxWidth={{ lg: "66.666%" }}
        >
            <ItemSlider {...props} />
        </Flex.Col>
    );
};

export default DynamicItemSlider;
